/**
 * Offline Data Store - IndexedDB-based caching for instant offline access
 * Provides persistent storage for health data that survives app restarts
 */

import { logger } from '@/lib/logger';

const DB_NAME = 'lupuslogix-offline';
const DB_VERSION = 1;

// Tables we want to cache for offline access
export type CacheableTable = 
  | 'prescriptions'
  | 'medications'
  | 'symptoms'
  | 'appointments'
  | 'vital_signs'
  | 'daily_moods'
  | 'daily_fatigue'
  | 'side_effects'
  | 'care_team_members'
  | 'user_settings';

interface CacheEntry<T = unknown> {
  key: string;
  data: T;
  timestamp: number;
  userId: string;
}

interface CacheMeta {
  table: CacheableTable;
  lastSync: number;
  userId: string;
}

class OfflineDataStore {
  private db: IDBDatabase | null = null;
  private dbPromise: Promise<IDBDatabase> | null = null;
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Initialize the IndexedDB database
   */
  private async initDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        logger.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store for cached data
        if (!db.objectStoreNames.contains('cache')) {
          const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
          cacheStore.createIndex('table', 'table', { unique: false });
          cacheStore.createIndex('userId', 'userId', { unique: false });
          cacheStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Create object store for cache metadata (last sync times)
        if (!db.objectStoreNames.contains('cacheMeta')) {
          const metaStore = db.createObjectStore('cacheMeta', { keyPath: ['table', 'userId'] });
          metaStore.createIndex('lastSync', 'lastSync', { unique: false });
        }
      };
    });

    return this.dbPromise;
  }

  /**
   * Generate a cache key for a table query
   */
  private getCacheKey(table: CacheableTable, userId: string, queryParams?: string): string {
    return `${table}:${userId}${queryParams ? `:${queryParams}` : ''}`;
  }

  /**
   * Store data in the cache
   */
  async set<T>(
    table: CacheableTable,
    userId: string,
    data: T,
    queryParams?: string
  ): Promise<void> {
    try {
      const db = await this.initDB();
      const key = this.getCacheKey(table, userId, queryParams);

      const transaction = db.transaction(['cache', 'cacheMeta'], 'readwrite');
      const cacheStore = transaction.objectStore('cache');
      const metaStore = transaction.objectStore('cacheMeta');

      const entry: CacheEntry<T> & { table: CacheableTable } = {
        key,
        table,
        data,
        timestamp: Date.now(),
        userId,
      };

      const meta: CacheMeta = {
        table,
        lastSync: Date.now(),
        userId,
      };

      await Promise.all([
        new Promise<void>((resolve, reject) => {
          const request = cacheStore.put(entry);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        }),
        new Promise<void>((resolve, reject) => {
          const request = metaStore.put(meta);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        }),
      ]);

      logger.log(`Cached ${table} data for user ${userId.slice(0, 8)}...`);
    } catch (error) {
      logger.error('Failed to cache data:', error);
    }
  }

  /**
   * Get data from the cache
   */
  async get<T>(
    table: CacheableTable,
    userId: string,
    queryParams?: string
  ): Promise<{ data: T | null; isStale: boolean; timestamp: number | null }> {
    try {
      const db = await this.initDB();
      const key = this.getCacheKey(table, userId, queryParams);

      const transaction = db.transaction('cache', 'readonly');
      const store = transaction.objectStore('cache');

      const entry = await new Promise<CacheEntry<T> | undefined>((resolve, reject) => {
        const request = store.get(key);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (!entry) {
        return { data: null, isStale: true, timestamp: null };
      }

      const isStale = Date.now() - entry.timestamp > this.CACHE_DURATION;

      return {
        data: entry.data,
        isStale,
        timestamp: entry.timestamp,
      };
    } catch (error) {
      logger.error('Failed to get cached data:', error);
      return { data: null, isStale: true, timestamp: null };
    }
  }

  /**
   * Get all cached data for a table
   */
  async getAllForTable<T>(
    table: CacheableTable,
    userId: string
  ): Promise<T[]> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction('cache', 'readonly');
      const store = transaction.objectStore('cache');
      const index = store.index('table');

      const entries = await new Promise<CacheEntry<T>[]>((resolve, reject) => {
        const request = index.getAll(table);
        request.onsuccess = () => {
          const filtered = (request.result as CacheEntry<T>[])
            .filter(entry => entry.userId === userId);
          resolve(filtered);
        };
        request.onerror = () => reject(request.error);
      });

      // Return the most recent entry's data
      if (entries.length === 0) return [];
      
      const mostRecent = entries.reduce((latest, current) => 
        current.timestamp > latest.timestamp ? current : latest
      );

      return Array.isArray(mostRecent.data) ? mostRecent.data : [mostRecent.data];
    } catch (error) {
      logger.error('Failed to get all cached data:', error);
      return [];
    }
  }

  /**
   * Get last sync time for a table
   */
  async getLastSyncTime(table: CacheableTable, userId: string): Promise<number | null> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction('cacheMeta', 'readonly');
      const store = transaction.objectStore('cacheMeta');

      const meta = await new Promise<CacheMeta | undefined>((resolve, reject) => {
        const request = store.get([table, userId]);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      return meta?.lastSync ?? null;
    } catch (error) {
      logger.error('Failed to get last sync time:', error);
      return null;
    }
  }

  /**
   * Clear cache for a specific table
   */
  async clearTable(table: CacheableTable, userId: string): Promise<void> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction(['cache', 'cacheMeta'], 'readwrite');
      const cacheStore = transaction.objectStore('cache');
      const metaStore = transaction.objectStore('cacheMeta');

      // Get all entries for this table
      const index = cacheStore.index('table');
      const entries = await new Promise<CacheEntry[]>((resolve, reject) => {
        const request = index.getAll(table);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      // Delete entries for this user
      const deletePromises = entries
        .filter(entry => entry.userId === userId)
        .map(entry => new Promise<void>((resolve, reject) => {
          const request = cacheStore.delete(entry.key);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        }));

      // Delete metadata
      deletePromises.push(new Promise<void>((resolve, reject) => {
        const request = metaStore.delete([table, userId]);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }));

      await Promise.all(deletePromises);
      logger.log(`Cleared ${table} cache for user`);
    } catch (error) {
      logger.error('Failed to clear table cache:', error);
    }
  }

  /**
   * Clear all cached data for a user
   */
  async clearAllForUser(userId: string): Promise<void> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction(['cache', 'cacheMeta'], 'readwrite');
      const cacheStore = transaction.objectStore('cache');
      const metaStore = transaction.objectStore('cacheMeta');

      // Get all entries for this user
      const index = cacheStore.index('userId');
      const entries = await new Promise<CacheEntry[]>((resolve, reject) => {
        const request = index.getAll(userId);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      // Delete all entries
      const deletePromises = entries.map(entry => new Promise<void>((resolve, reject) => {
        const request = cacheStore.delete(entry.key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }));

      await Promise.all(deletePromises);

      // Clear all metadata for this user
      const cursor = metaStore.openCursor();
      await new Promise<void>((resolve, reject) => {
        cursor.onsuccess = (event) => {
          const result = (event.target as IDBRequest).result;
          if (result) {
            if (result.value.userId === userId) {
              result.delete();
            }
            result.continue();
          } else {
            resolve();
          }
        };
        cursor.onerror = () => reject(cursor.error);
      });

      logger.log('Cleared all cached data for user');
    } catch (error) {
      logger.error('Failed to clear all user cache:', error);
    }
  }

  /**
   * Clear stale cache entries (older than cache duration)
   */
  async clearStaleEntries(): Promise<void> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction('cache', 'readwrite');
      const store = transaction.objectStore('cache');
      const index = store.index('timestamp');

      const staleThreshold = Date.now() - this.CACHE_DURATION;

      const staleEntries = await new Promise<CacheEntry[]>((resolve, reject) => {
        const range = IDBKeyRange.upperBound(staleThreshold);
        const request = index.getAll(range);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      const deletePromises = staleEntries.map(entry => new Promise<void>((resolve, reject) => {
        const request = store.delete(entry.key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }));

      await Promise.all(deletePromises);
      
      if (staleEntries.length > 0) {
        logger.log(`Cleared ${staleEntries.length} stale cache entries`);
      }
    } catch (error) {
      logger.error('Failed to clear stale entries:', error);
    }
  }

  /**
   * Get storage usage stats
   */
  async getStorageStats(): Promise<{ entryCount: number; tables: string[] }> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction('cache', 'readonly');
      const store = transaction.objectStore('cache');

      const entries = await new Promise<CacheEntry[]>((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      const tables = [...new Set(entries.map(e => (e as CacheEntry & { table: string }).table))];

      return {
        entryCount: entries.length,
        tables,
      };
    } catch (error) {
      logger.error('Failed to get storage stats:', error);
      return { entryCount: 0, tables: [] };
    }
  }
}

export const offlineDataStore = new OfflineDataStore();
